'use client'
import { useCreditFormatters } from "@/hooks/use-credit-formatters"


import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Loader2 } from 'lucide-react'

import type { ShopifyTopupOption } from '@/lib/billing/topup/shopify-topup-service'
import { CreditLifetimeNote } from './credit-lifetime-note'
import { useTranslations } from "next-intl"


export function ShopifyTopupDialog({
  option,
  open,
  onOpenChange,
  onConfirm,
  pending,
  lifetimeMonths,
}: {
  option: ShopifyTopupOption | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: () => void
  pending: boolean
  lifetimeMonths: number | null
}) {
  const { formatCredits } = useCreditFormatters()

  const tUi = useTranslations("Billing.BillingShopifyTopupDialog")
  const tCommonUi = useTranslations("Common")

  if (!option) return null

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle><span><span key={tUi("add")}>{tUi("add")}</span></span> {formatCredits(option.creditMicrousd)} <span><span key={tUi("toYourBalance")}>{tUi("toYourBalance")}</span></span></AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2">
              <p>
                {option.label} <span><span key={tUi("willBeAddedToYourStoreSNext")}>{tUi("willBeAddedToYourStoreSNext")}</span></span>{' '}
                {formatCredits(option.creditMicrousd)} <span><span key={tUi("landsOnYourBalanceRightAway")}>{tUi("landsOnYourBalanceRightAway")}</span></span>
                <span>{option.bonusPercent > 0 && ` (includes your ${option.bonusPercent}% plan bonus)`}</span><span>.</span>
              </p>
              <p><span key={tUi("shopifyBillsItWithYourSubscriptionAtThe")}>{tUi("shopifyBillsItWithYourSubscriptionAtThe")}</span></p>
              <CreditLifetimeNote subject="Top-up credits" months={lifetimeMonths} />
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}><span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></AlertDialogCancel>
          <AlertDialogAction
            disabled={pending}
            onClick={(event) => {
              // Radix closes on click; the dialog has to stay up while the charge runs.
              event.preventDefault()
              onConfirm()
            }}
          >
            {pending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            <span><span key={tUi("confirm")}>{tUi("confirm")}</span></span> {option.label}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
